import type {
  RankOrderInteractionMode,
  RankOrderOptionDisplay,
  RankOrderQuestion,
} from '../../../../types/index';
import { QUESTION_TYPE } from '../../../../constants/questionTypes';
import type { ApiQuestion, ApiQuestionSetting } from '../../../../internalTypes';
import type { ResolvedQuestionShell } from '../shared/resolveQuestionShell';
import {
  applyChoiceRandomization,
  parseChoiceRandomization,
} from '../shared/choiceRandomization';
import { buildRankOrderOptions } from './buildRankOrderOptions';

function parseBooleanConfig(configs: ApiQuestionSetting[], key: string): boolean {
  return configs.find(config => config.key === key)?.value?.[0]?.toLowerCase() === 'true';
}

function mapInteractionMode(rawValue: string | undefined): RankOrderInteractionMode {
  if (rawValue?.toUpperCase() === 'DRAG_AND_DROP') {
    return 'dragAndDrop';
  }
  return 'dropdown';
}

function mapOptionDisplay(rawValue: string | undefined): RankOrderOptionDisplay {
  switch (rawValue?.toUpperCase()) {
    case 'TEXT_ONLY':
      return 'textOnly';
    case 'IMAGE_ONLY':
      return 'imageOnly';
    case 'TEXT_AND_IMAGE':
    default:
      return 'textAndImage';
  }
}

export function mapRankOrderQuestion(
  raw: ApiQuestion,
  shell: ResolvedQuestionShell,
  configs: ApiQuestionSetting[],
  language?: string
): RankOrderQuestion {
  const answerTypeConfig = configs.find(config => config.key === 'RANK_ORDER_ANSWER_TYPE');
  const optionTypeConfig = configs.find(config => config.key === 'OPTION_TYPE');

  const builtOptions = buildRankOrderOptions(raw, language);
  const randomization = parseChoiceRandomization(configs);
  const { options, shuffleOptions } = applyChoiceRandomization(builtOptions, randomization);

  return {
    ...shell,
    type: QUESTION_TYPE.RANK_ORDER,
    options,
    interactionMode: mapInteractionMode(answerTypeConfig?.value?.[0]),
    optionDisplay: mapOptionDisplay(optionTypeConfig?.value?.[0]),
    requireRankAll: parseBooleanConfig(configs, 'REQUIRE_RANK_ALL'),
    shuffleOptions,
  };
}
